import * as z from "zod";
import { decimal, pagination } from "../index";
import { code } from "./fields";

// The estimated and the actual margin of a season, per style (B-59, ADR-215): written by Operations once the campaign
// is over, read by Tomorrow to review the percentages of the Price Setup of the next season. A view of Tomorrow only:
// a brand never reads Tomorrow's margins. Every amount is in euro.

/**
 * What keeps a part of a style's margin from being calculated:
 * - `no_price_setup`: the brand has no active EXW price list in the Sales tab of the season;
 * - `no_estimate`: the line has no purchase and wholesale price on that list;
 * - `no_sales`: no unit of the style sold in the season's orders;
 * - `no_purchase`: no unit of the style in the season's purchase orders approved by Operations;
 * - `missing_rate`: a currency of a price or an order without Finance's rate to the euro for the season.
 */
export const styleMarginIssues = ["no_price_setup", "no_estimate", "no_sales", "no_purchase", "missing_rate"] as const;
export type StyleMarginIssue = (typeof styleMarginIssues)[number];

/** Calculates again the margins of every style of the season, replacing the ones calculated before. */
export const calculateStyleMarginsInput = z.object({ season: code });
export type CalculateStyleMarginsInput = z.input<typeof calculateStyleMarginsInput>;

/** How a calculation went: the styles written and when. */
export const styleMarginCalculationDto = z.object({
  season: z.string(),
  styles: z.number().int(),
  calculatedAt: z.iso.datetime(),
});

/** The margins of the styles of a season, by brand and style code. */
export const listStyleMarginsInput = z.object({
  season: code,
  /** Only the styles of this brand. */
  brand: z.uuid().optional(),
  /** Part of the style code, of the brand or of Tomorrow. */
  q: z.string().trim().min(1).max(100).optional(),
  ...pagination({ defaultSize: 50, maxSize: 200 }),
});
export type ListStyleMarginsInput = z.input<typeof listStyleMarginsInput>;

/** The margin of a style (a style-colour, a line of the linesheet), estimated and actual, in euro. */
export const styleMarginDto = z
  .object({
    productId: z.uuid(),
    brandOrgId: z.uuid(),
    brand: z.string(),
    styleCode: z.string(),
    itemCode: z.string().nullable(),
    styleName: z.string().nullable(),
    colourDescription: z.string().nullable(),
    /** The price list of the estimate: the first EXW list active in the Sales tab of the brand and season. */
    priceList: z.string().nullable(),
    /** Per unit, from the prices of the line on that list. */
    estimatedPurchase: decimal.nullable(),
    /** Per unit: freight in, duties in and warehousing of the Operations tab on the purchase price. */
    estimatedCosts: decimal.nullable(),
    estimatedWholesale: decimal.nullable(),
    /** (wholesale − purchase − costs) / wholesale, in percentage points with two decimals. */
    estimatedMarginPct: decimal.nullable(),
    unitsSold: z.number().int(),
    /** What the units sold were sold for, after the discounts of the line and of the order. */
    revenue: decimal.nullable(),
    unitsPurchased: z.number().int(),
    /** The units sold at the average unit cost of the purchase orders. */
    costOfSales: decimal.nullable(),
    /** Freight in, duties in and warehousing of the units sold: the Operations tab on their cost, until Finance (B-59). */
    landedCosts: decimal.nullable(),
    actualMargin: decimal.nullable(),
    actualMarginPct: decimal.nullable(),
    /** Actual minus estimated, in percentage points. */
    marginDeltaPct: decimal.nullable(),
    issues: z.array(z.enum(styleMarginIssues)),
  })
  .meta({ id: "StyleMargin" });
export type StyleMarginDto = z.output<typeof styleMarginDto>;

/** The margins of a season by brand, and of the whole season. */
export const getSeasonMarginsInput = z.object({ season: code });
export type GetSeasonMarginsInput = z.input<typeof getSeasonMarginsInput>;

/**
 * The margins of a brand, or of the season, over its styles. The estimate is weighted by the units sold, so that it
 * compares with the actual one; both count only the styles with what they need (`styles` counts them all).
 */
export const marginTotalsDto = z.object({
  styles: z.number().int(),
  /** Styles with both an estimate and an actual margin: the ones the two percentages compare. */
  compared: z.number().int(),
  unitsSold: z.number().int(),
  revenue: decimal,
  actualMargin: decimal,
  estimatedMarginPct: decimal.nullable(),
  actualMarginPct: decimal.nullable(),
  marginDeltaPct: decimal.nullable(),
});
export type MarginTotalsDto = z.output<typeof marginTotalsDto>;

export const seasonMarginsDto = z
  .object({
    season: z.string(),
    /** When the margins were last calculated; null if never. */
    calculatedAt: z.iso.datetime().nullable(),
    /** The name of who calculated them, null if never or for a user without a name. */
    calculatedBy: z.string().nullable(),
    total: marginTotalsDto,
    brands: z.array(marginTotalsDto.extend({ brandOrgId: z.uuid(), brand: z.string() })),
  })
  .meta({ id: "SeasonMargins" });
export type SeasonMarginsDto = z.output<typeof seasonMarginsDto>;
